import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ZiroIcons, ZiroStackedIcons } from '../../../shared/models/ziro-icons.enum';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ButtonSizes, ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { DistributionListsResource } from '../../../shared/resources/distribution-lists.resource';
import { get, sortBy } from 'lodash';
import { ActivatedRoute, Router } from '@angular/router';
import { VoicemailSearchResource } from '../../../shared/resources/voicemail-search.resource';
import {
  DistributionList,
  DistributionListMember,
  GlobalVoicemailResult,
} from '../../../shared/models/generated/ziroModels';
import { ToastService } from '../../../shared/services/toast.service';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { Observable, Subscription } from 'rxjs';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormConfig } from '../../../forms/ziro-forms-models';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';

interface SearchUserResult extends GlobalVoicemailResult {
  userExists: boolean;
}

interface DistributionListFormEntity {
  search: string;
}

interface AddMemberRow extends DatatableRow {
  displayName: string;
  alias: string;
  extension: string;
  id: string;
}

@Component({
  selector: 'ziro-add-member-to-distribution-list',
  templateUrl: './add-member-to-distribution-list.component.html',
  styleUrl: './add-member-to-distribution-list.component.scss',
  standalone: false,
})
export class AddMemberToDistributionListComponent
  extends ZiroFormAbstractDirective<DistributionListFormEntity>
  implements OnInit, OnDestroy
{
  protected _ziroFormStateService: ZiroFormStateService;
  private breadcrumbsService = inject(BreadcrumbsService);
  private toastService = inject(ToastService);
  private route = inject(ActivatedRoute);
  private distributionListsResource = inject(DistributionListsResource);
  private voicemailSearchResource = inject(VoicemailSearchResource);
  private router = inject(Router);

  columns: DatatableColumn<AddMemberRow>[];
  tableRows: AddMemberRow[] = [];
  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;
  ZiroStackedIcons = ZiroStackedIcons;
  ziroIcons = ZiroIcons;
  globalVoiceSearchResults: SearchUserResult[] = [];
  filteredGlobalVoiceSearchResults: SearchUserResult[] = [];
  userToAdd: SearchUserResult;
  distributionList: DistributionList;
  confirmAdd = false;
  isAddingUser = false;
  formConfig = {
    fields: {
      search: {
        dataAutomation: 'search-user-to-add-to-distribution-list',
        componentConfig: new ZiroTextConfig({
          htmlInputType: HtmlInputType.TEXT,
          placeholder: 'tkey;pages.distributionlist.add.search.title',
        }),
        required: true,
      },
    },
  } as ZiroFormConfig;

  private _serverId: string;
  private _distributionListId: number;
  private _request: { id: string };
  private _subscription = new Subscription();
  private _listMembers: DistributionListMember[];

  private static _filterByDisplayName(listMember: GlobalVoicemailResult, displayName: string): boolean {
    const filterDisplayName = displayName;
    return filterDisplayName ? listMember.displayName.toLowerCase().includes(filterDisplayName.toLowerCase()) : true;
  }

  private static _filterByAlias(listMember: GlobalVoicemailResult, alias: string): boolean {
    const filterAlias = alias;
    return filterAlias ? listMember.alias.toLowerCase().includes(filterAlias.toLowerCase()) : true;
  }

  private static _filterByExtension(listMember: GlobalVoicemailResult, extension: string): boolean {
    const filterExtension = extension;
    return filterExtension ? listMember.extension.toLowerCase().includes(filterExtension.toLowerCase()) : true;
  }

  ngOnInit(): void {
    this._serverId = get(this.route, 'snapshot.params.serverid');
    this._distributionListId = get(this.route, 'snapshot.params.distributionlistid');
    this._getDistributionListInfo();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscription.unsubscribe();
  }

  backToDistributionList(): void {
    const url = `server/${this._serverId}/distribution-list/${this._distributionListId}`;
    this.router.navigateByUrl(url);
  }

  onEnterSubmit(): void {
    this._validateAndSubmitSource.next(true);
  }

  private _getDistributionListInfo(): void {
    this.distributionListsResource
      .getDistributionList(this._serverId, this._distributionListId)
      .subscribe((distributionList) => {
        this.distributionList = distributionList;
        this._listMembers = distributionList.members;
        this._setBreadcrumbs();
      });
  }

  private _userExistsInList(searchUserResults: GlobalVoicemailResult[]): void {
    this.globalVoiceSearchResults = searchUserResults.map((result: GlobalVoicemailResult) => {
      const userExists = this._listMembers.find(
        (member: DistributionListMember) => result.alias === member.alias && result.displayName === member.displayName,
      );
      return { ...result, userExists: !!userExists };
    });
  }

  goToConfirmAddPage(value: boolean, member?: SearchUserResult): void {
    this.confirmAdd = value;
    if (value && !member.userExists) {
      this.userToAdd = member;
      this._request = {
        id: member.id,
      };
    }
  }

  addUserToList(): void {
    this.isAddingUser = true;
    this.distributionListsResource
      .addMemberToDistributionList(this._serverId, this._distributionListId, this._request)
      .subscribe(() => {
        this.toastService.push(
          ToastTypes.SUCCESS,
          this.ziroIcons.DISTRIBUTION_LIST_SMALL,
          'tkey;shared.toast.save.success.title',
          'tkey;pages.distributionlist.add.member.success',
          { member: this.userToAdd.displayName },
        );
        this.isAddingUser = false;
        this.backToDistributionList();
      });
  }

  protected submit(): Observable<void> {
    this.globalVoiceSearchResults = [];
    this.filteredGlobalVoiceSearchResults = [];
    return new Observable<void>((subscriber) => {
      this.voicemailSearchResource
        .searchGloballyByQ(this.formData.search, Number(this._serverId))
        .subscribe((globalVoiceMailResult) => {
          if (globalVoiceMailResult.length === 0) {
            this.globalVoiceSearchResults = [];
            this.filteredGlobalVoiceSearchResults = [];
          } else {
            this._userExistsInList(globalVoiceMailResult);
            this._filterResults();
            this._createTable();
            this._generateTableRows();
          }
          subscriber.next();
          subscriber.complete();
        });
    });
  }

  private _setBreadcrumbs(): void {
    this.breadcrumbsService.updateBreadcrumbs([
      {
        label: 'tkey;search.distribution.lists.section.header',
        url: `/app2/#/server/${this._serverId}/distribution-list/${this._distributionListId}`,
      },
      { label: this.distributionList.displayName },
    ]);
  }

  private _filterResults(): void {
    const filteredResult = this.globalVoiceSearchResults.filter((members) => {
      return (
        AddMemberToDistributionListComponent._filterByDisplayName(members, '') &&
        AddMemberToDistributionListComponent._filterByAlias(members, '') &&
        AddMemberToDistributionListComponent._filterByExtension(members, '')
      );
    });
    this.filteredGlobalVoiceSearchResults = sortBy(filteredResult, (data) => {
      return [data.displayName, data.extension];
    });
  }

  private _createTable(): void {
    this.columns = [
      {
        name: 'displayName',
        columnSize: 'col-sm-4',
        label: 'tkey;pages.distributionlist.display_name',
      },
      {
        name: 'alias',
        columnSize: 'col-sm-4',
        label: 'tkey;pages.distributionlist.alias',
      },
      {
        name: 'extension',
        columnSize: 'col-sm-3',
        label: 'tkey;pages.distributionlist.extension',
      },
    ];
  }

  private _generateTableRows(): void {
    this.tableRows = this.filteredGlobalVoiceSearchResults.map((searchUserResult) => {
      return {
        displayName: searchUserResult.displayName,
        alias: searchUserResult.alias,
        extension: searchUserResult.extension,
        id: searchUserResult.id,
        actions: [
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.PRIMARY,
            dataAutomation: 'add-member-to-distribution-list',
            icon: ZiroIcons.NEXT,
            onClick: () => this.goToConfirmAddPage(true, searchUserResult),
            disabledTooltip: 'tkey;pages.distributionlist.members.tooltip',
            disabled: searchUserResult.userExists,
          },
        ],
      };
    });
  }
}
